/**
 * Custom checkout — toast notification system zamiast top-of-page WC error notice.
 *
 * WC core flow przy AJAX failure:
 *   1. POST `/?wc-ajax=checkout` → response `{result: 'failure', messages: '<ul class=...><li>...</li></ul>'}`
 *   2. WC checkout.js wstrzykuje messages do `.woocommerce-NoticeGroup-checkout` na górze form'a
 *   3. WC anim-scrolluje do tego notice'a
 *   4. WC triggeruje `checkout_error` event na document.body z messages jako argumentem
 *
 * Nasz override:
 *   - Łapiemy `checkout_error` (jQuery event)
 *   - Parsujemy `<li>` z messages → toasty
 *   - Usuwamy `.woocommerce-NoticeGroup-checkout` + `.woocommerce-error` z DOM
 *   - Zatrzymujemy WC scroll animation (`$('html, body').stop(true)`)
 *
 * Toast UX: top-right, slide-in od góry, auto-dismiss 6s, klik ✕ żeby zamknąć od razu,
 * max 5 toastów (najstarsze usuwane gdy przekroczone).
 */
export function initCheckoutToast() {
    if (!window.jQuery) return;
    const $ = window.jQuery;

    // Container — singleton, lazy-created.
    let container = null;
    const getContainer = () => {
        if (!container || !document.body.contains(container)) {
            container = document.createElement('div');
            container.className = 'hn-toast-container';
            container.setAttribute('aria-live', 'polite');
            document.body.appendChild(container);
        }
        return container;
    };

    const showToast = (message, type = 'error') => {
        const c = getContainer();
        // Ten sam komunikat już wisi — nie duplikuj, tylko zwróć na niego uwagę.
        const istniejacy = Array.from(c.children).find((t) => {
            const el = t.querySelector('.hn-toast__text');
            return el && el.textContent === message;
        });
        if (istniejacy) {
            istniejacy.classList.remove('is-pulse');
            void istniejacy.offsetWidth;
            istniejacy.classList.add('is-pulse');
            return;
        }
        // Limit 5 — usuń najstarszy gdy przekroczone.
        while (c.children.length >= 5) {
            c.removeChild(c.firstChild);
        }
        const toast = document.createElement('div');
        toast.className = 'hn-toast hn-toast--' + type;
        toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

        const icon = document.createElement('span');
        icon.className = 'hn-toast__icon';
        icon.innerHTML = type === 'error' ? '⚠' : '✓';

        const text = document.createElement('span');
        text.className = 'hn-toast__text';
        text.textContent = message;

        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'hn-toast__close';
        close.setAttribute('aria-label', 'Zamknij');
        close.innerHTML = '✕';

        toast.appendChild(icon);
        toast.appendChild(text);
        toast.appendChild(close);
        c.appendChild(toast);

        // Animate in (next frame, żeby transition złapał z initial state).
        requestAnimationFrame(() => toast.classList.add('is-visible'));

        const dismiss = () => {
            toast.classList.remove('is-visible');
            toast.classList.add('is-leaving');
            setTimeout(() => toast.remove(), 220);
        };
        close.addEventListener('click', dismiss);
        // Błąd zostaje do zamknięcia — znikał po 6s i user nie wiedział, co poprawić.
        if (type !== 'error') {
            const timeout = setTimeout(dismiss, 6000);
            toast.addEventListener('mouseenter', () => clearTimeout(timeout));
        }
    };

    // Parse HTML notice → array string'ów wiadomości.
    const parseMessages = (html) => {
        if (!html) return [];
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const lis = tmp.querySelectorAll('li');
        if (lis.length === 0) {
            const text = tmp.textContent.trim();
            return text ? [text] : [];
        }
        return Array.from(lis).map((li) => li.textContent.trim()).filter(Boolean);
    };

    // Listener — WC firuje to po insert'cie notice'a + na początku scroll'a.
    $(document.body).on('checkout_error', function (event, messages) {
        const errors = parseMessages(messages);
        errors.forEach((msg) => showToast(msg, 'error'));

        // Usuń native WC notice z DOM żeby nie było duplikatu na górze.
        $('.woocommerce-NoticeGroup-checkout, form.checkout > .woocommerce-error, form.checkout > .woocommerce-message').remove();

        // Zatrzymaj WC scroll animation (`$('html, body').animate(...)`)
        // — animacja już ruszyła, ale możemy ją przerwać.
        $('html, body').stop(true);
    });

    // Też dla success notices / message przy update_checkout — opcjonalne.
    $(document.body).on('wc_checkout_message', function (event, message) {
        if (message) showToast(message, 'success');
    });
}
